import React from 'react'
import PropTypes from 'prop-types'
import { makeT, sharedT } from '../../../../group_leader/helpers/i18n'
import PlayerPickerList from './PlayerPickerList'
import PickerPopover from '../../common/PickerPopover'

const t = makeT('group_leader.event_setup.pairings')

// The search and cancel copy every picker list carries.
const pickerCopy = () => ({
  searchPlaceholder: t('picker.search'),
  searchAria: t('picker.search'),
  noMatchesText: t('picker.no_matches'),
  cancelLabel: sharedT('cancel'),
})

// The four popovers over the tee sheet; slotMenu re-points the same one via setOverlay.
const PairingsOverlays = ({ overlay, setOverlay, onClose, unplaced, allPlayers, addingPlayer, actions }) => (
  <>
    {overlay?.kind === 'slotPicker' && (
      <PickerPopover rect={overlay.rect} onClose={onClose} mobileSheet>
        <PlayerPickerList
          options={unplaced}
          disabled={addingPlayer}
          onSelect={(player) => actions.placeInGroup(player, overlay.foursomeId, overlay.slotIndex)}
          {...pickerCopy()}
          emptyText={t('picker.none_to_add')}
          headerAction={(
            <button
              className="gl-event-setup-picker-action"
              type="button"
              onClick={() => setOverlay({ ...overlay, kind: 'blindPicker' })}
            >
              <span className="gl-event-setup-picker-action-icon" aria-hidden="true">+</span>
              {t('slot_menu.add_blind')}
            </button>
          )}
          onCancel={onClose}
        />
      </PickerPopover>
    )}

    {overlay?.kind === 'slotMenu' && (
      <PickerPopover rect={overlay.rect} onClose={onClose} mobileSheet>
        {overlay.player?._type === 'blind' && (
          <>
            <button
              className="gl-event-setup-slot-picker-item"
              type="button"
              onClick={() => setOverlay({ ...overlay, kind: 'blindPicker' })}
            >
              {t('slot_menu.replace_blind')}
            </button>
            <button
              className="gl-event-setup-slot-picker-item"
              type="button"
              onClick={() => actions.removeBlind(overlay.player.blind_id)}
            >
              {t('slot_menu.remove_blind')}
            </button>
          </>
        )}
        {overlay.player && overlay.player._type !== 'blind' && (
          <>
            <button
              className="gl-event-setup-slot-picker-item"
              type="button"
              onClick={() => setOverlay({
                kind: 'swapPicker',
                playerRoundId: overlay.player.player_round_id,
                rect: overlay.rect,
              })}
            >
              {t('slot_menu.swap_player')}
            </button>
            <button
              className="gl-event-setup-slot-picker-item"
              type="button"
              onClick={() => setOverlay({
                ...overlay,
                kind: 'blindPicker',
                removePlayerId: overlay.player.player_round_id,
              })}
            >
              {t('slot_menu.swap_blind')}
            </button>
            {/* The seat's own X is desktop-only, so this is the way off the sheet on a phone. */}
            <button
              className="gl-event-setup-slot-picker-item is-danger"
              type="button"
              onClick={() => actions.removePlayer(overlay.player.player_round_id)}
            >
              {t('slot_menu.remove_player')}
            </button>
          </>
        )}
        <button className="gl-event-setup-slot-picker-cancel" type="button" onClick={onClose}>
          {sharedT('cancel')}
        </button>
      </PickerPopover>
    )}

    {overlay?.kind === 'blindPicker' && (
      <PickerPopover rect={overlay.rect} onClose={onClose} mobileSheet>
        <PlayerPickerList
          options={allPlayers.filter((p) => p.player_round_id && p.player_round_id !== overlay.removePlayerId)}
          // Replace blind arrives holding the blind; Add blind and Swap for blind do not.
          onSelect={(player) => (overlay.player?._type === 'blind'
            ? actions.replaceBlind(overlay.foursomeId, overlay.player, player.player_round_id)
            : actions.addBlind(overlay.foursomeId, overlay.slotIndex, player.player_round_id, overlay.removePlayerId))}
          {...pickerCopy()}
          onCancel={onClose}
        />
      </PickerPopover>
    )}

    {overlay?.kind === 'swapPicker' && (
      <PickerPopover rect={overlay.rect} onClose={onClose} mobileSheet>
        <PlayerPickerList
          options={allPlayers.filter((p) => p.player_round_id && p.player_round_id !== overlay.playerRoundId)}
          onSelect={(player) => actions.swap(overlay.playerRoundId, player.player_round_id)}
          {...pickerCopy()}
          onCancel={onClose}
        />
      </PickerPopover>
    )}
  </>
)

PairingsOverlays.propTypes = {
  // null when nothing is open; otherwise { kind, rect, ...kind-specific fields }.
  overlay: PropTypes.shape({
    kind: PropTypes.oneOf([ 'slotPicker', 'slotMenu', 'blindPicker', 'swapPicker' ]).isRequired,
    // The cell that opened it, so the popover can anchor itself.
    rect: PropTypes.object,
    // Where an empty seat is: slotPicker and slotMenu.
    foursomeId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    slotIndex: PropTypes.number,
    // The seated golfer or blind draw, for slotMenu.
    player: PropTypes.object,
    // swapPicker: who is being swapped out. blindPicker: whose seat the blind takes over.
    playerRoundId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    removePlayerId: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
  }),
  setOverlay: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // Golfers enrolled but not on the sheet -- what an empty seat can be filled from.
  unplaced: PropTypes.array.isRequired,
  // Everyone on the sheet, for the swap and blind-draw pickers.
  allPlayers: PropTypes.array.isRequired,
  addingPlayer: PropTypes.bool,
  actions: PropTypes.shape({
    placeInGroup: PropTypes.func.isRequired,
    addBlind: PropTypes.func.isRequired,
    replaceBlind: PropTypes.func.isRequired,
    removeBlind: PropTypes.func.isRequired,
    swap: PropTypes.func.isRequired,
    removePlayer: PropTypes.func.isRequired,
  }).isRequired,
}

export default PairingsOverlays
